import type { ControlIn, IControl } from "@/controls/control";
import type { CreateElement, VNode, ShallowReactive } from "vue";
import { shallowReactive } from "vue";

//
// Important notes on Control Construction
//
// It will be very common for each control constructor to invoke watchEffect.
// Therefore we have an absolute requirement that controls are NOT constructed within
// the continuation part of an async function - as these watchers would then be
// outside of a view context and would never be cleaned up.
//
// Control construction should be avoided within Render functions, as we have seen that
// lead to apparent reactive circular behaviour which locks the FE.
//
// Generally, in the constructor of one control we would construct all the required
// child controls. This would apply even if some of those children may only be used
// conditionally (for example based on screen size or some control in options). It is
// fine to construct a few controls which actually don't happen to be participating
// in rendering at the moment.
//
// Main reason for not creating child controls in a constructor would be where the
// possible child controls are not known at construct time, Ie the child controls
// are dynamic such as in a list control. In this case the preference is to create
// the child controls in something like a computed(), which will react to changes
// in requirements for the child controls but not circularly recreate them.
//

export type PartialQ<TBase> = Partial<TBase> | undefined;

export function extendDefaults<T1 extends {}, T2 extends {}>(a: T1, b: T2) {
  return Object.assign({}, a, b) as T1 & T2;
}

export const controlDefaults = {
  visible: true,
  enabled: true,
  readOnly: false,
};

export abstract class TpControl<TIn extends ControlIn> implements IControl {
  #in: ShallowReactive<TIn>;
  parent: IControl | null = null;

  abstract render(h: CreateElement): VNode;

  constructor(defaults: TIn, ...opts: PartialQ<TIn>[]) {
    this.#in = shallowReactive(Object.assign({}, defaults, ...opts) as TIn);
  }

  get in() {
    return this.#in;
  }

  resolveEnabled() {
    return (this.parent?.resolveEnabled() ?? true) && this.in.enabled;
  }

  resolveReadOnly() {
    return (this.parent?.resolveReadOnly() ?? false) || this.in.readOnly;
  }
}
